import {
  createHttpClientFromRaw,
  type HttpMethod,
  type HttpRequestOptions,
  type RawHttpResponse,
} from '@shared/api/http'
import { createQueryParams } from '@shared/api/util'
import { requestDeduplicator } from '@shared/util'
import axios from 'axios'
import { axiosRefreshToken } from 'src/helpers/axios-refresh-token'
import uniqid from 'uniqid'

export const publicHttpClient = createHttpClientFromRaw(
  async <TResponse>(
    url: string,
    method: HttpMethod,
    options?: HttpRequestOptions,
  ): Promise<RawHttpResponse<TResponse>> => {
    const queryParams = createQueryParams(options?.query)
    const fullUrl = `${url}${queryParams}`

    return requestDeduplicator.deduplicateRequest(
      fullUrl,
      method,
      async () => {
        const res = await axios.request<TResponse>({
          url: fullUrl,
          method,
          headers: options?.headers,
          data: options?.body,
        })

        let data = res.data
        const envelope = data as any
        if (envelope?.data) {
          data = envelope.data
          // Preserve optional list-envelope metadata (e.g. the `/v1/events`
          // truncation signal) by carrying it alongside the unwrapped array.
          // Only set when present, so other endpoints are unaffected.
          if (data && typeof data === 'object') {
            if (envelope.truncated !== undefined) {
              ;(data as any).truncated = envelope.truncated
            }
            if (envelope.total_in_range !== undefined) {
              ;(data as any).total_in_range = envelope.total_in_range
            }
            if (envelope.returned_count !== undefined) {
              ;(data as any).returned_count = envelope.returned_count
            }
          }
        }

        return {
          status: res.status,
          data: data,
        }
      },
      options?.body,
    )
  },
)

// Create a single axios instance that's reused for all authorized requests
// This ensures axiosRefreshToken is only called once, avoiding initialization issues
let authorizedAxiosClientInstance: ReturnType<typeof axios.create> | null = null

function getAuthorizedAxiosClient() {
  if (!authorizedAxiosClientInstance) {
    authorizedAxiosClientInstance = axios.create()
    axiosRefreshToken(authorizedAxiosClientInstance)
  }
  return authorizedAxiosClientInstance
}

export const authorizedHttpClient = createHttpClientFromRaw(
  async <TResponse>(
    url: string,
    method: HttpMethod,
    options?: HttpRequestOptions,
  ): Promise<RawHttpResponse<TResponse>> => {
    const authorizedAxiosClient = getAuthorizedAxiosClient()

    const queryParams = createQueryParams(options?.query)
    const fullUrl = `${url}${queryParams}`

    return requestDeduplicator.deduplicateRequest(
      fullUrl,
      method,
      async () => {
        const token = localStorage.getItem('token')

        const headers: Record<string, string> = {
          ...(options?.headers ?? {}),
        }

        if (token) {
          headers['authorization'] = `Bearer ${token}`
        }

        if (fullUrl.includes(import.meta.env.VITE_API_URL)) {
          const id = uniqid()
          headers['sentry-trace'] = id
        }

        const res = await authorizedAxiosClient.request<TResponse>({
          url: fullUrl,
          method,
          headers,
          data: options?.body,
        })

        let data = res.data
        const envelope = data as any
        if (envelope?.data) {
          data = envelope.data
          // Preserve optional list-envelope metadata (e.g. the `/v1/events`
          // truncation signal) by carrying it alongside the unwrapped array.
          // Only set when present, so other endpoints are unaffected.
          if (data && typeof data === 'object') {
            if (envelope.truncated !== undefined) {
              ;(data as any).truncated = envelope.truncated
            }
            if (envelope.total_in_range !== undefined) {
              ;(data as any).total_in_range = envelope.total_in_range
            }
            if (envelope.returned_count !== undefined) {
              ;(data as any).returned_count = envelope.returned_count
            }
          }
        }

        return {
          status: res.status,
          data: data,
        }
      },
      options?.body,
    )
  },
)
